// Shareable score card: renders a 1200x630 pixel-art image of a run result and shares it.
// On mobile it opens the native share sheet (image + text + link); on desktop it downloads
// the image and opens an X compose. Every share is a branded ad pointing at brewmancer.online.

import { avatarCanvas } from '../render/pixelArt';
import { flagEmoji } from '../net/leaderboardClient';

const SITE = 'https://brewmancer.online';

export interface ShareData {
  score: number;
  kills: number;
  seconds: number;
  level: number;
  victory: boolean;
  rank?: number | null;
  name?: string;
  avatar?: number;
  country?: string | null;
}

const F = (px: number, weight = ''): string => `${weight} ${px}px "Silkscreen", ui-monospace, monospace`.trim();

export function renderScoreCard(d: ShareData): HTMLCanvasElement {
  const W = 1200;
  const H = 630;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const ctx = c.getContext('2d');
  if (ctx === null) return c;

  // Background + frame.
  ctx.fillStyle = '#14101c';
  ctx.fillRect(0, 0, W, H);
  const g = ctx.createRadialGradient(W / 2, H / 2, 120, W / 2, H / 2, 720);
  g.addColorStop(0, 'rgba(60,40,110,0.30)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = '#4a4366';
  ctx.lineWidth = 8;
  ctx.strokeRect(14, 14, W - 28, H - 28);

  ctx.textAlign = 'center';
  ctx.imageSmoothingEnabled = false;

  // Title.
  ctx.fillStyle = '#a78bfa';
  ctx.font = F(72, 'bold');
  ctx.fillText('BREWMANCER', W / 2, 108);

  // Result.
  ctx.fillStyle = d.victory ? '#9fffa0' : '#ff8a98';
  ctx.font = F(40);
  ctx.fillText(d.victory ? 'VICTORY!' : 'GAME OVER', W / 2, 168);

  // Avatar (centered, enlarged + crisp).
  const av = avatarCanvas(d.avatar ?? 0, 9); // 9x12 grid -> 81x108
  ctx.drawImage(av, Math.round(W / 2 - av.width / 2), 188);

  // Score.
  ctx.fillStyle = '#9a93b0';
  ctx.font = F(24);
  ctx.fillText('SCORE', W / 2, 350);
  ctx.fillStyle = '#ffd34d';
  ctx.font = F(96, 'bold');
  ctx.fillText(d.score.toLocaleString(), W / 2, 440);

  // Rank + name (when submitted).
  let y = 492;
  if (d.rank !== undefined && d.rank !== null) {
    ctx.fillStyle = '#ffd34d';
    ctx.font = F(34);
    const who = d.name !== undefined && d.name !== '' ? ` ${flagEmoji(d.country ?? null)} ${d.name}` : '';
    ctx.fillText(`RANK #${d.rank}${who}`, W / 2, y);
    y += 44;
  }

  // Stats line.
  const mm = Math.floor(d.seconds / 60).toString().padStart(2, '0');
  const ss = Math.floor(d.seconds % 60).toString().padStart(2, '0');
  ctx.fillStyle = '#cdc7dd';
  ctx.font = F(26);
  ctx.fillText(`${mm}:${ss}  ·  ${d.kills} kills  ·  level ${d.level}`, W / 2, y);

  // CTA.
  ctx.fillStyle = '#ffd24a';
  ctx.font = F(30, 'bold');
  ctx.fillText('Play free at brewmancer.online', W / 2, 588);

  return c;
}

/** Build the card and share it. Mobile: native sheet with the image. Desktop: download + X. */
export async function shareScore(d: ShareData): Promise<void> {
  try {
    await document.fonts.ready; // make sure Silkscreen is loaded before drawing to canvas
  } catch {
    /* ignore */
  }
  const canvas = renderScoreCard(d);
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/png'));
  if (blob === null) return;

  const rankTxt = d.rank !== undefined && d.rank !== null ? ` (rank #${d.rank})` : '';
  const text = `I scored ${d.score.toLocaleString()} on Brewmancer${rankTxt}. Can you beat it? ⚗️`;
  const file = new File([blob], 'brewmancer-score.png', { type: 'image/png' });

  const nav = navigator as Navigator & { canShare?: (data: { files: File[] }) => boolean };
  if (typeof navigator.share === 'function' && nav.canShare?.({ files: [file] }) === true) {
    try {
      await navigator.share({ files: [file], text, url: SITE });
    } catch {
      /* user cancelled */
    }
    return;
  }

  // Desktop fallback: download the image so it can be attached, and open an X compose.
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = 'brewmancer-score.png';
  a.click();
  URL.revokeObjectURL(objUrl);
  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${text} ${SITE}`)}`, '_blank', 'noopener');
}
